<template>
	<div class="formGroup withIcon" style="position: relative">
		<label v-if="label" class="textGrey">{{ label }}:</label>
		<div class="formGroupInput flex h-[36.5px] items-center rounded pl-2" @click="toggleDropdown">
			<div v-if="multiSelect && selectedValues && selectedValues.length" class="flex flex-wrap gap-1">
				<div
					v-for="(val, index) in selectedDisplay"
					:key="index"
					class="flex items-center rounded bg-blue-100 px-2 py-1 text-sm dark:bg-blue-900"
				>
					{{ val }}
					<span class="fa-light fa-times ml-1 cursor-pointer" @click.stop="removeSelected(val)"></span>
				</div>
				<span v-if="selectedValues.length > 2">+{{ selectedValues.length - 2 }}</span>
			</div>
			<span v-else-if="selectedOption">{{ selectedOption }}</span>
			<span v-else>{{ placeholder }}</span>
			<span
				class="fa-light absolute right-3 z-20 text-green"
				:class="{ 'fa-chevron-up': isOpen, 'fa-chevron-down': !isOpen }"
			></span>
		</div>
		<Transition
			enter-active-class="transition ease-out duration-200"
			enter-from-class="transform opacity-0 scale-95"
			enter-to-class="transform opacity-100 scale-100"
			leave-active-class="transition ease-in duration-150"
			leave-from-class="transform opacity-100 scale-100"
			leave-to-class="transform opacity-0 scale-95"
		>
			<div
				v-if="isOpen"
				class="absolute z-[1001] mt-1 w-full rounded-md border border-gray-300 bg-white shadow-lg dark:bg-[#2C323C]"
			>
				<div class="sticky top-0 z-10 bg-white p-2 dark:bg-[#2C323C]">
					<input
						v-if="isSearchable"
						ref="searchInput"
						v-model="searchQuery"
						type="text"
						:placeholder="searchPlaceholder"
						class="w-full p-2"
						@click.stop
					/>
					<div
						v-if="showDefaultOption"
						class="cursor-pointer border-b border-gray-200 p-3 italic text-gray-600 dark:text-gray-400"
						@click="selectOption(null)"
					>
						{{ defaultOptionText }}
					</div>
				</div>
				<div class="overflow-auto" style="max-height: 200px">
					<div
						v-for="option in filteredOptions"
						:key="option.key || option.value"
						class="flex cursor-pointer items-center justify-between p-3 hover:bg-black/10 dark:hover:bg-black/30"
						@click.stop="selectOption(option)"
					>
						{{ option.label }}
						<span v-if="isSelected(option.value)" class="fa-light fa-check text-blue-500"></span>
					</div>
					<div v-if="filteredOptions.length === 0" class="p-3 text-center italic text-gray-500">
						No results found
					</div>
				</div>
			</div>
		</Transition>
	</div>
</template>

<script>
export default {
	name: 'SearchableDropdown',
	props: {
		options: {
			type: Array,
			required: true
		},
		modelValue: {
			type: [String, Number, Object, Array],
			default: null
		},
		label: {
			type: String,
			default: ''
		},
		placeholder: {
			type: String,
			default: '-- Select an option --'
		},
		searchPlaceholder: {
			type: String,
			default: 'Search...'
		},
		isSearchable: {
			type: Boolean,
			default: true
		},
		showDefaultOption: {
			type: Boolean,
			default: true
		},
		defaultOptionText: {
			type: String,
			default: '-- Select an option --'
		},
		multiSelect: {
			type: Boolean,
			default: true
		}
	},

	data() {
		return {
			isOpen: false,
			searchQuery: '',
			selectedValues: Array.isArray(this.modelValue)
				? [...this.modelValue]
				: this.modelValue
					? [this.modelValue]
					: []
		}
	},

	computed: {
		filteredOptions() {
			if (!this.searchQuery) {
				return this.normalisedOptions
			}

			const query = this.searchQuery.toLowerCase()
			return this.normalisedOptions.filter((option) => option.label.toLowerCase().includes(query))
		},
		normalisedOptions() {
			return this.options.map((option) => {
				if (typeof option === 'object' && option !== null) {
					return {
						key: option.key || option.value,
						value: option.value !== undefined ? option.value : option,
						label:
							option.label ||
							option.name ||
							option.text ||
							option.companyName ||
							(option.id ? `ID: ${option.id}` : JSON.stringify(option))
					}
				} else {
					return {
						key: option,
						value: option,
						label: option.toString()
					}
				}
			})
		},
		selectedOption() {
			if (!this.selectedValues.length) return null

			if (this.multiSelect) {
				return `${this.selectedValues.length} selected`
			}

			const option = this.findOptionByValue(this.selectedValues[0])
			if (option) return option.label
			if (typeof this.selectedValues[0] === 'string') return this.selectedValues[0]
			return null
		},
		selectedDisplay() {
			if (!this.selectedValues.length) return []

			return this.selectedValues.slice(0, 2).map((value) => {
				const option = this.findOptionByValue(value)
				return option ? option.label : this.getReadableValueLabel(value)
			})
		}
	},

	watch: {
		modelValue(newVal) {
			this.selectedValues = Array.isArray(newVal) ? [...newVal] : newVal ? [newVal] : []
		},
		isOpen(val) {
			if (val && this.isSearchable) {
				this.$nextTick(() => {
					if (this.$refs.searchInput) {
						this.$refs.searchInput.focus()
					}
				})
			}
		}
	},

	beforeUnmount() {
		document.removeEventListener('click', this.closeDropdown)
	},

	methods: {
		toggleDropdown() {
			this.isOpen = !this.isOpen
			if (this.isOpen) {
				this.searchQuery = ''
				document.addEventListener('click', this.closeDropdown)
			} else {
				document.removeEventListener('click', this.closeDropdown)
			}
		},
		closeDropdown(event) {
			if (!this.$el.contains(event.target)) {
				this.isOpen = false
				document.removeEventListener('click', this.closeDropdown)
			}
		},
		selectOption(option) {
			const value = option ? option.value : null

			if (this.multiSelect && value) {
				const index = this.selectedValues.findIndex((v) => this.areValuesEqual(v, value))

				if (index === -1) {
					this.selectedValues.push(value)
				} else {
					this.selectedValues.splice(index, 1)
				}
			} else {
				this.selectedValues = value ? [value] : []
			}

			const emitValue = this.multiSelect
				? this.selectedValues
				: this.selectedValues.length
					? this.selectedValues[0]
					: null
			this.$emit('update:modelValue', emitValue)
			this.$emit('change', emitValue)

			if (!this.multiSelect) {
				this.isOpen = false
			}
		},
		removeSelected(label) {
			const option = this.normalisedOptions.find((opt) => opt.label === label)
			if (option) {
				const index = this.selectedValues.findIndex((v) => this.areValuesEqual(v, option.value))

				if (index !== -1) {
					this.selectedValues.splice(index, 1)
					this.$emit('update:modelValue', this.selectedValues)
					this.$emit('change', this.selectedValues)
				}
			}
		},
		isSelected(value) {
			if (!this.selectedValues.length) return false
			return this.selectedValues.some((val) => this.areValuesEqual(val, value))
		},
		findOptionByValue(value) {
			return this.normalisedOptions.find((opt) => this.areValuesEqual(opt.value, value))
		},
		areValuesEqual(val1, val2) {
			if (val1 === val2) return true

			if (typeof val1 === 'object' && typeof val2 === 'object' && val1 !== null && val2 !== null) {
				if (val1.id !== undefined && val2.id !== undefined) {
					return val1.id === val2.id
				}

				return JSON.stringify(val1) === JSON.stringify(val2)
			}

			return false
		},
		getReadableValueLabel(value) {
			if (value === null || value === undefined) return this.placeholder

			if (typeof value === 'object') {
				return value.name || value.companyName || value.label || (value.id ? `ID: ${value.id}` : 'Selected')
			}

			return value.toString()
		}
	}
}
</script>
